import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory';
import Coordinate from 'jsts/org/locationtech/jts/geom/Coordinate';
import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp';
import {
  MultiPolygon2,
  Point2,
  Polyline2,
} from 'src/domain/geometry/geometric-types';
import { toJstsMultiPolygon } from 'src/domain/geometry/algorithms/util/polygon';

/**
 * Pattern types for polygon fills
 */
export enum HatchPatternType {
  None = 'none',
  DiagonalLines = 'diagonal',
  CrossHatch = 'crosshatch',
}

/**
 * Configuration for a hatch pattern
 */
export type HatchPatternConfig = {
  type: HatchPatternType;
  /** Angle in degrees (0 = horizontal, 90 = vertical) */
  angle: number;
  /** Gap between lines in meters (SI units). Will be converted to sheet coordinates during rendering. */
  gap: number;
  /** Line width */
  lineWidth?: number;
  /** For crosshatch, the second angle (defaults to angle + 90) */
  secondAngle?: number;
};

/**
 * Default hatch pattern configurations
 */
export const DefaultDiagonalPattern: HatchPatternConfig = {
  type: HatchPatternType.DiagonalLines,
  angle: 45,
  gap: 0.5, // 0.5 meters
  lineWidth: 0.02,
};

export const DefaultCrossHatchPattern: HatchPatternConfig = {
  type: HatchPatternType.CrossHatch,
  angle: 45,
  gap: 0.5, // 0.5 meters
  lineWidth: 0.02,
};

/**
 * Generates hatch lines for a polygon based on the pattern configuration.
 * Returns an array of polylines that should be drawn on top of the polygon.
 */
export function generateHatchLines(
  multiPolygon: MultiPolygon2,
  config: HatchPatternConfig
): Polyline2[] {
  if (config.type === HatchPatternType.None) {
    return [];
  }

  const lines: Polyline2[] = [];

  // Generate lines for the primary angle
  const primaryLines = generateParallelLinesForPolygon(
    multiPolygon,
    config.angle,
    config.gap
  );
  lines.push(...primaryLines);

  // For crosshatch, generate lines for the secondary angle
  if (config.type === HatchPatternType.CrossHatch) {
    const secondAngle = config.secondAngle ?? config.angle + 90;
    const secondaryLines = generateParallelLinesForPolygon(
      multiPolygon,
      secondAngle,
      config.gap
    );
    lines.push(...secondaryLines);
  }

  return lines;
}

/**
 * Generates parallel lines at a given angle that fill a polygon.
 * The lines are clipped to the polygon boundary.
 */
function generateParallelLinesForPolygon(
  multiPolygon: MultiPolygon2,
  angleDegrees: number,
  gap: number
): Polyline2[] {
  if (multiPolygon.polygons.length === 0) {
    return [];
  }

  // Calculate bounding box of the entire multipolygon
  const bounds = calculateBounds(multiPolygon);
  if (!bounds) {
    return [];
  }

  const { minX, minY, maxX, maxY } = bounds;

  // Calculate the diagonal length of the bounding box
  // This ensures lines are long enough to cover the entire polygon at any angle
  const diagonal = Math.sqrt(
    Math.pow(maxX - minX, 2) + Math.pow(maxY - minY, 2)
  );

  // Center of the bounding box
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Convert angle to radians
  const angleRad = (angleDegrees * Math.PI) / 180;

  // Direction perpendicular to the lines (for stepping)
  const perpX = Math.cos(angleRad + Math.PI / 2);
  const perpY = Math.sin(angleRad + Math.PI / 2);

  // Direction along the lines
  const lineX = Math.cos(angleRad);
  const lineY = Math.sin(angleRad);

  // Generate lines
  const unclippedLines: [Point2, Point2][] = [];

  // Number of lines needed to cover the diagonal
  const numLines = Math.ceil(diagonal / gap) + 2;
  const startOffset = -numLines / 2;

  for (let i = 0; i <= numLines; i++) {
    const offset = (startOffset + i) * gap;

    // Calculate a point on this line (at the center, offset perpendicular to line direction)
    const px = centerX + perpX * offset;
    const py = centerY + perpY * offset;

    // Calculate start and end points of the line (extending past the bounding box)
    const halfLength = diagonal;
    const startPoint: Point2 = [
      px - lineX * halfLength,
      py - lineY * halfLength,
    ];
    const endPoint: Point2 = [px + lineX * halfLength, py + lineY * halfLength];

    unclippedLines.push([startPoint, endPoint]);
  }

  // Clip lines to the polygon using JSTS
  const clippedLines = clipLinesToPolygon(unclippedLines, multiPolygon);
  return clippedLines;
}

/**
 * Calculate the bounding box of a multipolygon
 */
function calculateBounds(multiPolygon: MultiPolygon2): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let hasPoints = false;

  for (const polygon of multiPolygon.polygons) {
    for (const point of polygon.exterior) {
      hasPoints = true;
      minX = Math.min(minX, point[0]);
      minY = Math.min(minY, point[1]);
      maxX = Math.max(maxX, point[0]);
      maxY = Math.max(maxY, point[1]);
    }
  }

  if (!hasPoints) {
    return null;
  }

  return { minX, minY, maxX, maxY };
}

/**
 * Clips line segments to a polygon using JSTS.
 * Returns the portions of the lines that are inside the polygon.
 */
function clipLinesToPolygon(
  lines: [Point2, Point2][],
  multiPolygon: MultiPolygon2
): Polyline2[] {
  const result: Polyline2[] = [];
  const geometryFactory = new GeometryFactory();

  try {
    // Convert multipolygon to JSTS geometry
    const jstsPolygon = toJstsMultiPolygon(multiPolygon);

    for (const [start, end] of lines) {
      // Create JSTS line
      const lineCoords = [
        new Coordinate(start[0], start[1]),
        new Coordinate(end[0], end[1]),
      ];
      const jstsLine = geometryFactory.createLineString(lineCoords);

      // Compute intersection using OverlayOp
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let intersection: any;
      try {
        intersection = OverlayOp.intersection(jstsLine, jstsPolygon);
      } catch {
        // Skip lines that fail to intersect (can happen with degenerate geometries)
        continue;
      }

      if (intersection && !intersection.isEmpty()) {
        // Extract line segments from the intersection result
        const extractedLines = extractLinesFromGeometry(intersection);
        result.push(...extractedLines);
      }
    }
  } catch (error) {
    console.warn('Error clipping hatch lines to polygon:', error);
  }

  return result;
}

/**
 * Extracts line segments from a JSTS geometry result.
 * The intersection can return various geometry types.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractLinesFromGeometry(geometry: any): Polyline2[] {
  const lines: Polyline2[] = [];

  const geometryType = geometry.getGeometryType();

  if (geometryType === 'LineString') {
    const coords = geometry.getCoordinates();
    if (coords.length >= 2) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      lines.push(coords.map((c: any) => [c.x, c.y] as Point2));
    }
  } else if (geometryType === 'MultiLineString') {
    const numGeometries = geometry.getNumGeometries();
    for (let i = 0; i < numGeometries; i++) {
      const lineString = geometry.getGeometryN(i);
      const coords = lineString.getCoordinates();
      if (coords.length >= 2) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        lines.push(coords.map((c: any) => [c.x, c.y] as Point2));
      }
    }
  } else if (geometryType === 'GeometryCollection') {
    const numGeometries = geometry.getNumGeometries();
    for (let i = 0; i < numGeometries; i++) {
      const subGeom = geometry.getGeometryN(i);
      lines.push(...extractLinesFromGeometry(subGeom));
    }
  }
  // Ignore Point and other types - they don't contribute to hatch lines

  return lines;
}

/**
 * Creates Edit2D polyline shapes from hatch lines.
 * These shapes can be added to the Edit2D layer as overlays.
 */
export function createHatchShapes(
  hatchLines: Polyline2[],
  convertPointToRender: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  },
  style: {
    lineColor: string;
    lineWidth: number;
  }
): Autodesk.Edit2D.Shape[] {
  const shapes: Autodesk.Edit2D.Shape[] = [];

  for (const line of hatchLines) {
    if (line.length < 2) continue;

    // Convert to render coordinates
    const convertedPoints = line.map((point) =>
      convertPointToRender({ x: point[0], y: point[1] })
    );

    // Create SVG path for the polyline
    const pathData =
      'M ' + convertedPoints.map((p) => `${p.x},${p.y}`).join(' L ');

    const svg = `<path d="${pathData}"/>`;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

    shape.style = {
      lineColor: style.lineColor,
      lineAlpha: 1,
      lineWidth: style.lineWidth,
      fillColor: 'transparent',
      fillAlpha: 0,
    };

    shapes.push(shape);
  }

  return shapes;
}
